import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Box,
  Pagination,
  Grid,
  Typography,
  TextField,
  Button,
  Drawer,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import theme from "../../../theme";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import {
  createMarginGroup,
  getMarginGroups,
  updateMarginGroup,
  deleteMarginGroup,
} from "../../../services/core-api";
import { MarginGroup } from "../../../interfaces/core/MarginGroup";
import { useSnackbar } from "../../../components/common/SnackbarContext";

const MarginGroupsPage = () => {
  const [page, setPage] = React.useState(1);
  const rowsPerPage = 10;
  const [count, setCount] = React.useState(0);
  const [groups, setGroups] = React.useState<MarginGroup[]>([]);
  const [searchValue, setSearchValue] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<MarginGroup | null>(null);
  const [loading, setLoading] = React.useState(false);

  const [name, setName] = React.useState("");
  const [percentage, setPercentage] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [isActive, setIsActive] = React.useState(true);
  const { showSnackbar } = useSnackbar();

  const fetchGroups = React.useCallback(
    async (options?: { page?: number; keyword?: string }) => {
      const nextPage = options?.page ?? page;
      const nextKeyword = options?.keyword ?? searchValue;
      try {
        setLoading(true);
        const response = await getMarginGroups({
          keyword: nextKeyword,
          page_number: nextPage,
          limit: rowsPerPage,
        });
        setGroups(response.margin_groups || []);
        setPage(response.page_number || nextPage);
        setCount(Math.max(1, Math.ceil((response.total || 0) / rowsPerPage)));
      } catch {
        setGroups([]);
        setPage(1);
        setCount(0);
      } finally {
        setLoading(false);
      }
    },
    [page, searchValue]
  );

  React.useEffect(() => {
    fetchGroups();
  }, [page, searchValue]);

  const resetForm = () => {
    setName("");
    setPercentage("");
    setDescription("");
    setIsActive(true);
  };

  const handleCreate = async () => {
    if (!name.trim() || percentage === "") {
      showSnackbar("Name and margin percentage are required.", "error");
      return;
    }
    try {
      const created = await createMarginGroup({
        name: name.trim(),
        margin_percentage: parseFloat(percentage),
        description: description.trim() || undefined,
        is_active: isActive,
      });
      showSnackbar("Margin group created.", "success");
      setOpen(false);
      resetForm();

      // Show new records immediately: clear search, go to first page, refetch uncached list
      setSearchValue("");
      setPage(1);
      if (created?.id) {
        setGroups((prev) => {
          const withoutDup = prev.filter((g) => g.id !== created.id);
          return [created, ...withoutDup];
        });
      }
      await fetchGroups({ page: 1, keyword: "" });
    } catch (error: any) {
      showSnackbar(
        error?.response?.data?.detail || "Failed to create margin group.",
        "error"
      );
    }
  };

  const openEdit = (group: MarginGroup) => {
    setEditing(group);
    setName(group.name);
    setPercentage(String(group.margin_percentage));
    setDescription(group.description || "");
    setIsActive(group.is_active);
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editing) return;
    try {
      await updateMarginGroup(editing.id, {
        name: name.trim(),
        margin_percentage: parseFloat(percentage),
        description: description.trim(),
        is_active: isActive,
      });
      showSnackbar("Margin group updated.", "success");
      setEditOpen(false);
      setEditing(null);
      resetForm();
      await fetchGroups();
    } catch (error: any) {
      showSnackbar(
        error?.response?.data?.detail || "Failed to update margin group.",
        "error"
      );
    }
  };

  const handleStatusToggle = async (group: MarginGroup) => {
    try {
      await updateMarginGroup(group.id, { is_active: !group.is_active });
      showSnackbar("Status updated.", "success");
      await fetchGroups();
    } catch (error: any) {
      showSnackbar(
        error?.response?.data?.detail || "Failed to update status.",
        "error"
      );
    }
  };

  const handleDelete = async (group: MarginGroup) => {
    try {
      await deleteMarginGroup(group.id);
      showSnackbar("Margin group deleted.", "success");
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      await fetchGroups();
    } catch (error: any) {
      showSnackbar(
        error?.response?.data?.detail || "Failed to delete margin group.",
        "error"
      );
    }
  };

  const formFields = (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <TextField
          placeholder="Group name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ sx: { fontSize: "13px", padding: "10px 14px" } }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          placeholder="Margin (%)"
          fullWidth
          type="number"
          value={percentage}
          onChange={(e) => setPercentage(e.target.value)}
          inputProps={{
            sx: { fontSize: "13px", padding: "10px 14px" },
            min: 0,
            step: "0.01",
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          placeholder="Description (optional)"
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ sx: { fontSize: "13px", padding: "10px 14px" } }}
        />
      </Grid>
      <Grid item xs={12}>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            }
            label="Active"
          />
        </FormGroup>
      </Grid>
    </Grid>
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography
            variant="body1"
            sx={{
              fontSize: "18px",
              float: "left",
              fontFamily: "Inter",
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 3,
            }}
          >
            Margin Groups
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={8}>
          <TextField
            placeholder="Search"
            variant="outlined"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setPage(1);
            }}
            sx={{ width: "100%" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4} sx={{ textAlign: "right" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
          >
            Add Margin Group
          </Button>
        </Grid>
      </Grid>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Margin %</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Clients</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No margin groups found
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell>{group.name}</TableCell>
                    <TableCell>{Number(group.margin_percentage)}%</TableCell>
                    <TableCell>{group.description || "—"}</TableCell>
                    <TableCell>{group.client_count ?? 0}</TableCell>
                    <TableCell>
                      <Switch
                        checked={group.is_active}
                        onChange={() => handleStatusToggle(group)}
                        disabled={group.name === "No margin"}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ mr: 1 }}
                        onClick={() => openEdit(group)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        disabled={group.name === "No margin"}
                        onClick={() => handleDelete(group)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            count={count}
            page={page}
            onChange={(_e, p) => setPage(p)}
            color="primary"
          />
        </Box>
      </Paper>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
      >
        <Box sx={{ p: 3, mt: 4 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>
            Create Margin Group
          </Typography>
          {formFields}
          <Button
            sx={{
              width: "100%",
              mt: 2,
              bgcolor: theme.palette.primary.dark,
              color: theme.palette.text.secondary,
              "&:hover": { bgcolor: theme.palette.primary.dark },
            }}
            onClick={handleCreate}
          >
            Create
          </Button>
        </Box>
      </Drawer>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Margin Group</DialogTitle>
        <DialogContent>{formFields}</DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MarginGroupsPage;
